import React from 'react';

// Maps every client-facing delivery status to a label + DS state-token pair.
// Mirrors next-portal-v2-web's DeliveryStatusBadge colour mapping, expressed
// with this repo's semantic tokens (bg-*-lighter / text-*-dark).
const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  PENDING_PAYMENT: { label: 'Pending Payment', bg: 'bg-error-lighter', text: 'text-error-dark' },
  QUEUEING: { label: 'Queueing', bg: 'bg-faded-lighter', text: 'text-faded-dark' },
  ALLOCATING: { label: 'Allocating', bg: 'bg-information-lighter', text: 'text-information-dark' },
  PENDING_PICKUP: { label: 'Pending Pickup', bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  PICKING_UP: { label: 'Picking Up', bg: 'bg-away-lighter', text: 'text-away-dark' },
  CREATED: { label: 'Created', bg: 'bg-information-lighter', text: 'text-information-dark' },
  PREPARING: { label: 'Preparing', bg: 'bg-information-lighter', text: 'text-information-dark' },
  PENDING_DELIVERY: { label: 'Pending Delivery', bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  IN_DELIVERY: { label: 'In Delivery', bg: 'bg-away-lighter', text: 'text-away-dark' },
  VERIFIED: { label: 'Verified', bg: 'bg-success-lighter', text: 'text-success-dark' },
  NOT_VERIFIED: { label: 'Not Verified', bg: 'bg-faded-lighter', text: 'text-faded-dark' },
  COMPLETED: { label: 'Completed', bg: 'bg-success-lighter', text: 'text-success-dark' },
  FAILED: { label: 'Failed', bg: 'bg-error-lighter', text: 'text-error-dark' },
  CANCELLED: { label: 'Cancelled', bg: 'bg-faded-lighter', text: 'text-faded-dark' },
  PENDING_RETURN: { label: 'Pending Return', bg: 'bg-highlighted-lighter', text: 'text-highlighted-dark' },
  IN_RETURN: { label: 'In Return', bg: 'bg-feature-lighter', text: 'text-feature-dark' },
  RETURNED: { label: 'Returned', bg: 'bg-information-lighter', text: 'text-information-dark' },
  FAILED_IN_RETURN: { label: 'Failed in Return', bg: 'bg-error-lighter', text: 'text-error-dark' },
  ON_HOLD: { label: 'On Hold', bg: 'bg-faded-lighter', text: 'text-faded-dark' },
  DISPOSED: { label: 'Disposed', bg: 'bg-error-lighter', text: 'text-error-dark' },
  EXPIRED: { label: 'Expired', bg: 'bg-faded-lighter', text: 'text-faded-dark' },
};

const toTitleCase = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Human-readable label for a delivery status (used by the status filter too).
export const getDeliveryStatusLabel = (status: string): string =>
  STATUS_CONFIG[status]?.label ?? toTitleCase(status || 'Unknown');

interface DeliveryStatusBadgeProps {
  status: string;
  className?: string;
}

export const DeliveryStatusBadge: React.FC<DeliveryStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const config = STATUS_CONFIG[status] ?? {
    label: toTitleCase(status || 'Unknown'),
    bg: 'bg-bg-weak-50',
    text: 'text-text-sub-600',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text} ${className}`.trim()}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {config.label}
    </span>
  );
};

export default DeliveryStatusBadge;
